import { styled } from "@mui/material/styles";
import { Box } from "@mui/material";
import { COLORS } from "../../shared/theme/colorConfig";
import tokens from "../../shared/theme/tokens";

export const AvailabilityLayout = styled(Box)(
  ({ theme }) => `
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing(2)};
  padding: ${theme.spacing(3)};
  max-width: 1400px;
  margin: 0 auto;
`
);

export const FilterBarWrap = styled(Box)(
  ({ theme }) => `
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing(1)};
  align-items: center;
  padding: ${theme.spacing(1.5)} ${theme.spacing(2)};
  background: white;
  border: 1px solid ${COLORS.gray[200]};
  border-radius: 8px;
`
);

export const FilterPill = styled("button", {
  shouldForwardProp: (prop) => prop !== "active",
})<{ active?: number }>(
  ({ theme, active }) => `
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: ${theme.spacing(0.75)} ${theme.spacing(1.5)};
  border: 1px solid ${active ? COLORS.primary.main : COLORS.gray[200]};
  background: ${active ? COLORS.blue[50] : "white"};
  color: ${active ? COLORS.primary.main : COLORS.gray[900]};
  font-size: 13px;
  font-weight: ${active ? 600 : 500};
  border-radius: 20px;
  cursor: pointer;
  transition: background 0.1s, border-color 0.1s;
  &:hover { background: ${COLORS.gray[50]}; }
`
);

export const Sidebar = styled(Box)(
  ({ theme }) => `
  background: ${COLORS.gray[50]};
  border: 1px solid ${COLORS.gray[200]};
  border-radius: 8px;
  padding: ${theme.spacing(2)};
  height: fit-content;
  position: sticky;
  top: ${theme.spacing(2)};
`
);

export const SidebarSectionTitle = styled("h4")(
  ({ theme }) => `
  margin: 0 0 ${theme.spacing(1)} 0;
  font-size: 14px;
  font-weight: 600;
  color: ${COLORS.gray[900]};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`
);

export const FloorplanRow = styled(Box)(
  ({ theme }) => `
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: ${theme.spacing(2)};
`
);

export const FloorplanCardWrap = styled(Box)(
  ({ theme }) => `
  border: 1px solid ${COLORS.gray[200]};
  border-radius: 8px;
  overflow: hidden;
  background: white;
  display: flex;
  flex-direction: column;
  position: relative;
  transition: box-shadow 0.15s;
  &:hover {
    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
  }
`
);

export const SpecialRibbon = styled(Box)(
  ({ theme }) => `
  position: absolute;
  top: 12px;
  right: -28px;
  background: ${tokens.colors.error.main};
  color: white;
  padding: 4px 32px;
  transform: rotate(35deg);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1px;
  z-index: 1;
`
);

export const FloorplanImageWrap = styled(Box)(
  ({ theme }) => `
  width: 100%;
  aspect-ratio: 16 / 9;
  background: ${COLORS.gray[100]};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`
);

export const FloorplanBody = styled(Box)(
  ({ theme }) => `
  padding: ${theme.spacing(2)};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing(1)};
  flex: 1;
`
);

export const StatsGrid = styled(Box)(
  ({ theme }) => `
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing(0.5)};
  font-size: 13px;
`
);

export const StatLabel = styled("span")(
  () => `
  color: ${tokens.colors.inkMuted};
  font-size: 12px;
`
);

export const StatValue = styled("span")(
  () => `
  font-weight: 600;
`
);

export const ChargesTable = styled("table")(
  ({ theme }) => `
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  th, td {
    padding: ${theme.spacing(0.75)} 0;
    border-bottom: 1px solid ${COLORS.gray[100]};
    text-align: left;
  }
  th { color: ${COLORS.gray[600]}; font-weight: 500; }
  td:last-child { text-align: right; }
  th:last-child { text-align: right; }
`
);

export const EmptyState = styled(Box)(
  ({ theme }) => `
  padding: ${theme.spacing(4)};
  text-align: center;
  color: ${COLORS.gray[600]};
  border: 1px dashed ${COLORS.gray[300]};
  border-radius: 8px;
  background: ${COLORS.gray[50]};
`
);

export const FloorplanList = styled(Box)(
  ({ theme }) => `
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing(1.5)};
  width: 100%;
  ${theme.breakpoints.up("md")} {
    max-width: 960px;
    margin: 0 auto;
  }
`
);

export const FloorplanRowWrap = styled(Box)(
  () => `
  border: 1px solid ${COLORS.gray[200]};
  border-radius: 8px;
  background: white;
  overflow: hidden;
  position: relative;
`
);

export const FloorplanRowHeader = styled(Box)(
  ({ theme }) => `
  display: grid;
  grid-template-columns: 88px minmax(180px, 1.4fr) repeat(6, minmax(64px, 1fr)) auto;
  align-items: center;
  gap: ${theme.spacing(2)};
  padding: ${theme.spacing(1.5)} ${theme.spacing(2)};
  background: ${COLORS.gray[50]};
  ${theme.breakpoints.down("md")} {
    grid-template-columns: 72px 1fr auto;
    row-gap: ${theme.spacing(1)};
  }
`
);

export const FloorplanThumb = styled(Box)(
  ({ theme }) => `
  width: 88px;
  height: 66px;
  background: white;
  border: 1px solid ${COLORS.gray[100]};
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  img, svg { width: 100%; height: 100%; object-fit: cover; }
  ${theme.breakpoints.down("md")} {
    width: 72px; height: 54px;
  }
`
);

export const StatCell = styled(Box)(
  () => `
  display: flex;
  flex-direction: column;
  min-width: 0;
  font-size: 13px;
  .label { color: ${COLORS.gray[600]}; font-size: 11px; text-transform: uppercase; letter-spacing: 0.4px; }
  .value { font-weight: 600; color: ${COLORS.gray[900]}; }
`
);

export const RowTitle = styled(Box)(
  () => `
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  .title { font-weight: 700; font-size: 15px; color: ${COLORS.gray[900]}; }
  .subtitle { font-size: 12px; color: ${COLORS.gray[600]}; }
  .expand { font-size: 12px; color: ${COLORS.blue[500]}; cursor: pointer; margin-top: 2px; background: none; border: 0; padding: 0; text-align: left;
    display: inline-flex; align-items: center; gap: 4px; }
  .expand:hover { text-decoration: underline; }
  .last { font-size: 12px; color: ${COLORS.gray[500]}; font-style: italic; }
`
);

export const RowActions = styled(Box)(
  ({ theme }) => `
  display: flex;
  gap: ${theme.spacing(1)};
  align-items: center;
`
);

export const ExpandedUnitsWrap = styled(Box)(
  () => `
  background: white;
  border-top: 1px solid ${COLORS.gray[100]};
`
);
